/**
 * Display helpers for the person panel. Dates render in UTC so the same
 * timestamp reads the same for every recruiter and in every test run.
 */
import type { PersonAppearance, PersonDetail } from "@nu-assist/contracts";

const DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});
const DATE_TIME = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "UTC",
});
const MONTH = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `Aug 5, 2026`; empty for a missing or unparseable value. */
export function fmtDate(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? DATE.format(d) : "";
}

/** `Aug 5, 2026, 10:00 AM` (UTC). */
export function fmtDateTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? `${DATE_TIME.format(d)} UTC` : "";
}

/** `YYYY-MM` → `Mar 2022`; null → `Present` (an open-ended job). */
export function fmtMonth(ym: string | null | undefined): string {
  if (!ym) return "Present";
  const d = parse(`${ym}-01T00:00:00Z`);
  return d ? MONTH.format(d) : ym;
}

/** Whole months from `start` to `end` (or `today`), at least 1. Null when the
 *  start is unknown. */
export function monthsBetween(
  start: string | null | undefined,
  end: string | null | undefined,
  today: Date = new Date()
): number | null {
  if (!start) return null;
  const [y1, m1] = start.split("-").map(Number);
  if (!y1 || !m1) return null;
  const [y2, m2] = end
    ? end.split("-").map(Number)
    : [today.getUTCFullYear(), today.getUTCMonth() + 1];
  if (!y2 || !m2) return null;
  return Math.max(1, (y2 - y1) * 12 + (m2 - m1));
}

/** `27` → `2 yrs 3 mos`. */
export function fmtDuration(months: number | null): string {
  if (months == null) return "";
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y ? `${y} yr${y > 1 ? "s" : ""}` : "", m ? `${m} mo${m > 1 ? "s" : ""}` : ""]
    .filter(Boolean)
    .join(" ");
}

/** Whole days between an instant and `today` (never negative). */
export function daysAgo(iso: string | null | undefined, today: Date = new Date()): number | null {
  const d = parse(iso);
  if (!d) return null;
  return Math.max(0, Math.floor((today.getTime() - d.getTime()) / 86_400_000));
}

/** `3` → `3 days ago`, `0` → `today`. */
export function fmtDaysAgo(days: number | null): string {
  if (days == null) return "";
  if (days === 0) return "today";
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export function personName(person: PersonDetail): string {
  const { first_name, last_name } = person.identity;
  return [first_name, last_name].filter(Boolean).join(" ").trim() || person.handle;
}

const HAS_LETTER = /\p{L}/u;

/** The whitespace tokens of a name that carry at least one letter — a stored
 *  name can lead with a symbol ("☆ Mark Hoekstra", a real local row), and a
 *  token like "☆" is decoration, not a name. */
function letterTokens(name: string): string[] {
  return name
    .trim()
    .split(/\s+/)
    .filter((t) => HAS_LETTER.test(t));
}

/** The first name copy uses in sentences ("Everything that happened to
 *  Mark"): the first letter-bearing token of the display name, else the full
 *  display name (a name of symbols only reads as stored). The ONE helper for
 *  every first-name interpolation in the panel and the contact page. */
export function displayFirstName(displayName: string): string {
  return letterTokens(displayName)[0] ?? displayName.trim();
}

/** Monogram initials: the first letter of each of the first two
 *  letter-bearing tokens, upper-cased ("☆ Mark Hoekstra" → "MH", "Jane" →
 *  "J"). Empty when the name has no letter, so the avatar falls back to its
 *  own default. */
export function displayInitials(displayName: string): string {
  return letterTokens(displayName)
    .slice(0, 2)
    .map((t) => t.match(/\p{L}/u)?.[0] ?? "")
    .join("")
    .toUpperCase();
}

/** `displayFirstName` of the person's display name (first + last as stored). */
export function personFirstName(person: PersonDetail): string {
  return displayFirstName(personName(person));
}

/** `displayInitials` of the person's display name. */
export function personInitials(person: PersonDetail): string {
  return displayInitials(personName(person));
}

/**
 * The caption when the role is missing, or null when it is not. The line
 * talks about the role only — it never predicts what Thrive receives (the
 * export also sends the email and, for a company-only row, an older titled
 * job block). A row without a LinkedIn URL never opens the panel, so LinkedIn
 * is never named as missing. Whitespace-only counts as missing, like the API's
 * `_present()` in `build_identity`.
 *
 * `scope` is a parameter rather than the panel's `mode` so the helper stays
 * pure and its eight variants test without a render; the panel passes `mode`.
 * "for this list" belongs to a list: the page has no row to speak of.
 */
export function missingRoleCopy(
  role: { current_position?: string | null; current_company?: string | null },
  scope: "list" | "page"
): string | null {
  const noPosition = !role.current_position?.trim();
  const noCompany = !role.current_company?.trim();
  if (!noPosition && !noCompany) return null;
  const what =
    noPosition && noCompany ? "position or company" : noPosition ? "position" : "company";
  const where = scope === "list" ? " for this list" : "";
  return `No ${what} on file${where}.`;
}

/** `imports.source` as a recruiter says it. */
export function sourceLabel(source: string | null | undefined): string {
  switch (source) {
    case "baby_chops":
      return "ChopLink";
    case "csv_upload":
      return "CSV upload";
    case "manual":
      return "Manual";
    case "bct":
      return "Company Map";
    case "ai_review":
      return "AI Review";
    case "thrive_search":
      return "Thrive search";
    default:
      return source ?? "";
  }
}

/** Where a job was seen, as the panel's dates line says it (04): "LinkedIn",
 *  "Thrive" or "LinkedIn · Thrive"; null when the API said nothing. */
export function jobSourceLabel(
  sources: readonly ("linkedin" | "thrive")[] | null | undefined
): string | null {
  const parts: string[] = [];
  if (sources?.includes("linkedin")) parts.push("LinkedIn");
  if (sources?.includes("thrive")) parts.push("Thrive");
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** `Northwind Labs · Chief Technology Officer`, else the list name. */
export function searchLabel(appearance: PersonAppearance): string {
  const s = appearance.search;
  if (!s) return appearance.list_name;
  const client = s.client_company_name?.trim();
  const title = s.job_title?.trim() || s.name?.trim();
  return [client, title].filter(Boolean).join(" · ") || s.name || `Search ${s.id_thrive}`;
}

/** Group key for "one search": the Thrive search, else the list itself. */
export function searchKey(appearance: PersonAppearance): string {
  return appearance.search
    ? `search:${appearance.search.id_thrive}`
    : `list:${appearance.import_id}`;
}

/** One colour per search, in the order the person's lists first mention it.
 *  Full class names on purpose: Tailwind's JIT only emits classes it can read
 *  literally in source. */
const SEARCH_DOT = [
  "bg-chart-1",
  "bg-chart-2",
  "bg-chart-3",
  "bg-chart-4",
  "bg-chart-5",
  "bg-chart-6",
  "bg-chart-7",
  "bg-chart-8",
] as const;
const SEARCH_RING = [
  "border-chart-1",
  "border-chart-2",
  "border-chart-3",
  "border-chart-4",
  "border-chart-5",
  "border-chart-6",
  "border-chart-7",
  "border-chart-8",
] as const;

export interface SearchColour {
  dot: string;
  ring: string;
}

export function searchColours(person: PersonDetail): Map<string, SearchColour> {
  const map = new Map<string, SearchColour>();
  for (const a of person.appearances ?? []) {
    const key = searchKey(a);
    if (map.has(key)) continue;
    const i = map.size % SEARCH_DOT.length;
    map.set(key, { dot: SEARCH_DOT[i] ?? "bg-chart-neutral", ring: SEARCH_RING[i] ?? "" });
  }
  return map;
}

// ---------------------------------------------------------------------------
// AI Review model calls
// ---------------------------------------------------------------------------

/** The Bedrock cross-region profile a model id or route names (`us.`,
 *  `global.`, …), or null for a bare model id. */
export function routeBadge(route: string | null | undefined): string | null {
  const m = route?.trim().match(/^(us|eu|apac|global)\./i);
  return m?.[1] ? `${m[1].toLowerCase()}.` : null;
}

const FAMILY = /claude-([a-z]+)-(\d+)(?:-(\d{1,2})(?!\d))?/i;

/** `us.anthropic.claude-haiku-4-5-20251001-v1:0` → `Claude Haiku 4.5`; an id
 *  that does not read as a Claude model comes back as written. */
export function modelLabel(modelId: string | null | undefined): string {
  const id = modelId?.trim() ?? "";
  const m = id.match(FAMILY);
  if (!m?.[1] || !m[2]) return id;
  const family = m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
  return `Claude ${family} ${m[3] ? `${m[2]}.${m[3]}` : m[2]}`;
}

/** `1` → `1st`, `2` → `2nd`, `11` → `11th`, `23` → `23rd`. */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffix = { 1: "st", 2: "nd", 3: "rd" }[n % 10] ?? "th";
  return `${n}${suffix}`;
}

const INTEGER = new Intl.NumberFormat("en-US");

/** `9412` → `9,412`. */
export function fmtCount(n: number): string {
  return INTEGER.format(n);
}

/** Milliseconds → `24.8 s`. */
export function fmtSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`;
}

/** `0.02481` → `$0.025`; sub-tenth-of-a-cent costs keep a fourth digit. */
export function fmtCost(usd: number): string {
  return `$${usd.toFixed(usd < 0.001 ? 4 : 3)}`;
}
